import { z } from 'zod';
/** The tree parents, in navigation order. */
export const SERVICE_PARENTS = [
    { code: 'DASHBOARD_ROOT', nameAr: 'لمحة عامة', nameEn: 'Overview' },
    { code: 'ACCOUNTS_ROOT', nameAr: 'عملاء الذمم', nameEn: 'Debtors' },
    { code: 'MOVEMENTS_ROOT', nameAr: 'الحركات', nameEn: 'Movements' },
    { code: 'MESSAGES_ROOT', nameAr: 'الرسائل', nameEn: 'Messages' },
    { code: 'REPORTS_ROOT', nameAr: 'التقارير', nameEn: 'Reports' },
    { code: 'TRASH_ROOT', nameAr: 'سلة المحذوفات', nameEn: 'Trash' },
    { code: 'SETTINGS_ROOT', nameAr: 'إعدادات الذمم', nameEn: 'Debts settings' },
    { code: 'USERS_ROOT', nameAr: 'المستخدمين', nameEn: 'Users' },
    { code: 'GROUPS_ROOT', nameAr: 'المجموعات', nameEn: 'Groups' },
];
const ACCOUNT_SEEN = ['ACCOUNT_LIST', 'ACCOUNT_VIEW'];
const ROWS = [
    ['DASHBOARD_VIEW', 'DASHBOARD', 'DASHBOARD_ROOT', 'عرض لمحة عامة', 'View overview'],
    ['ACCOUNT_LIST', 'ACCOUNT', 'ACCOUNTS_ROOT', 'عرض قائمة عملاء الذمم', 'List debtors'],
    ['ACCOUNT_VIEW', 'ACCOUNT', 'ACCOUNTS_ROOT', 'عرض كشف العميل', 'View customer statement'],
    ['ACCOUNT_CREATE', 'ACCOUNT', 'ACCOUNTS_ROOT', 'إضافة عميل', 'Add customer'],
    ['ACCOUNT_UPDATE', 'ACCOUNT', 'ACCOUNTS_ROOT', 'تعديل العميل', 'Edit customer'],
    ['ACCOUNT_DELETE', 'ACCOUNT', 'ACCOUNTS_ROOT', 'حذف العميل', 'Delete customer'],
    ['ACCOUNT_EXPORT', 'ACCOUNT', 'ACCOUNTS_ROOT', 'تصدير عملاء الذمم', 'Export debtors'],
    ['MOVEMENT_CREATE', 'MOVEMENT', 'MOVEMENTS_ROOT', 'إضافة حركة', 'Add movement', ACCOUNT_SEEN],
    ['MOVEMENT_UPDATE', 'MOVEMENT', 'MOVEMENTS_ROOT', 'تعديل الحركة', 'Edit movement', ACCOUNT_SEEN],
    ['MOVEMENT_DELETE', 'MOVEMENT', 'MOVEMENTS_ROOT', 'حذف الحركة', 'Delete movement', ACCOUNT_SEEN],
    [
        'MOVEMENT_EXPORT',
        'MOVEMENT',
        'MOVEMENTS_ROOT',
        'تصدير الحركات',
        'Export movements',
        ACCOUNT_SEEN,
    ],
    ['MESSAGE_RETRY', 'MESSAGE', 'MESSAGES_ROOT', 'إعادة إرسال الرسالة', 'Resend message'],
    ['REPORT_VIEW', 'REPORT', 'REPORTS_ROOT', 'عرض التقارير', 'View reports'],
    ['REPORT_EXPORT', 'REPORT', 'REPORTS_ROOT', 'تصدير التقارير', 'Export reports'],
    ['TRASH_LIST', 'TRASH', 'TRASH_ROOT', 'عرض سلة المحذوفات', 'View trash'],
    ['TRASH_RESTORE', 'TRASH', 'TRASH_ROOT', 'استعادة من سلة المحذوفات', 'Restore from trash'],
    ['SETTINGS_VIEW', 'SETTINGS', 'SETTINGS_ROOT', 'عرض إعدادات الذمم', 'View debts settings'],
    ['SETTINGS_UPDATE', 'SETTINGS', 'SETTINGS_ROOT', 'تعديل إعدادات الذمم', 'Edit debts settings'],
    ['USER_LIST', 'USER', 'USERS_ROOT', 'عرض قائمة المستخدمين', 'List users'],
    ['USER_VIEW', 'USER', 'USERS_ROOT', 'عرض تفاصيل المستخدم', 'View user'],
    ['USER_CREATE', 'USER', 'USERS_ROOT', 'إضافة مستخدم', 'Create user'],
    ['USER_UPDATE', 'USER', 'USERS_ROOT', 'تعديل المستخدم', 'Update user'],
    ['USER_CHANGE_STATUS', 'USER', 'USERS_ROOT', 'تغيير حالة المستخدم', 'Change user status'],
    ['USER_RESET_PASSWORD', 'USER', 'USERS_ROOT', 'إعادة تعيين كلمة المرور', 'Reset password'],
    ['USER_DELETE', 'USER', 'USERS_ROOT', 'حذف المستخدم', 'Delete user'],
    ['GROUP_LIST', 'GROUP', 'GROUPS_ROOT', 'عرض قائمة المجموعات', 'List groups'],
    ['GROUP_VIEW', 'GROUP', 'GROUPS_ROOT', 'عرض تفاصيل المجموعة', 'View group'],
    ['GROUP_CREATE', 'GROUP', 'GROUPS_ROOT', 'إضافة مجموعة', 'Create group'],
    ['GROUP_UPDATE', 'GROUP', 'GROUPS_ROOT', 'تعديل المجموعة', 'Update group'],
    ['GROUP_CHANGE_STATUS', 'GROUP', 'GROUPS_ROOT', 'تغيير حالة المجموعة', 'Change group status'],
    ['GROUP_DELETE', 'GROUP', 'GROUPS_ROOT', 'حذف المجموعة', 'Delete group'],
    ['GROUP_SERVICE_MANAGE', 'GROUP', 'GROUPS_ROOT', 'تعيين خدمات المجموعة', 'Assign group services'],
];
const CODES = new Set(ROWS.map(([code]) => code));
function defaultDependencies(code, entity) {
    const list = `${entity}_LIST`;
    const view = `${entity}_VIEW`;
    if (code === list)
        return [];
    if (code === view)
        return CODES.has(list) ? [list] : [];
    return [list, view].filter((c) => CODES.has(c));
}
export const SERVICE_CATALOG = ROWS.map(([code, entity, parentCode, nameAr, nameEn, deps]) => ({
    code,
    entity,
    parentCode,
    nameAr,
    nameEn,
    dependsOn: deps ?? defaultDependencies(code, entity),
}));
export const SERVICE_CODES = ROWS.map(([code]) => code);
export const serviceCodeSchema = z.string().refine((v) => CODES.has(v), 'unknown service code');
/**
 * Every code a selection implies, transitively (SH-08, SH-09). Unknown codes
 * are dropped.
 */
export function serviceClosure(selected) {
    const byCode = new Map(SERVICE_CATALOG.map((s) => [s.code, s]));
    const result = new Set();
    const stack = selected.filter((c) => byCode.has(c));
    while (stack.length > 0) {
        const code = stack.pop();
        if (result.has(code))
            continue;
        result.add(code);
        for (const dep of byCode.get(code)?.dependsOn ?? [])
            stack.push(dep);
    }
    return [...result].toSorted();
}
/**
 * Seeded groups (debts.md section 13). مدير النظام is the built-in system group
 * holding every service; it cannot be deleted or emptied (SH-10). موظف الذمم
 * matches the legacy split where only the Super Admin edited and deleted
 * movements. Groups are edited freely afterwards.
 */
export const SEEDED_GROUPS = [
    {
        key: 'SYSTEM_ADMIN',
        nameAr: 'مدير النظام',
        nameEn: 'System administrator',
        services: SERVICE_CODES,
    },
    {
        key: 'DEBTS_EMPLOYEE',
        nameAr: 'موظف الذمم',
        nameEn: 'Debts employee',
        services: serviceClosure([
            'DASHBOARD_VIEW',
            'ACCOUNT_CREATE',
            'ACCOUNT_UPDATE',
            'ACCOUNT_EXPORT',
            'MOVEMENT_CREATE',
            'MOVEMENT_EXPORT',
            'MESSAGE_RETRY',
        ]),
    },
];
//# sourceMappingURL=contract.js.map